import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Portal } from '@mui/material';
import { TextField } from '@totalsoft/rocket-ui';
import LabeledCheckbox from 'components/common/inputs/LabeledCheckbox';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { parseId, valueOrDefault } from 'utils/functions';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import IconButton from 'components/common/buttons/IconButton';
import { useMutation } from 'hooks/apollo/useMutation';
import { SAVE_CITY } from 'features/city/mutations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { required, shape } from '@totalsoft/pure-validations';
import { CityType } from 'features/city/constants';
import useSubscription from 'hooks/apollo/useSubscription';
import { CITY_SAVED } from 'features/city/subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { shouldRefetchListVar } from 'features/city/variables';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_CITIES_WRITE } from 'constants/rightsCodes';

const validator = shape({
  code: required,
  name: required,
  nameInt: required
});

const CityEditComponent = ({ data, actionsSlotId, saveIconSize = 'small', onCitySaved, onDirty }) => {
  const { t } = useTranslation();
  const addToast = useToast();

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(data);

  const [validation, validate] = useDirtyFieldValidation(validator);

  const [pending, setPending] = useState(false);
  const canWrite = usePermission(ADMIN_CITIES_WRITE);

  useEffect(() => {
    resetLens(data);
  }, [data, resetLens]);

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useEffect(() => {
    if (!onDirty || !isDirty) return;
    onDirty();
  }, [isDirty, onDirty]);

  // #region Save

  const [saveCity] = useMutation(SAVE_CITY, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    const sanitizedData = { ...data };
    sanitizedData.id = parseId(sanitizedData.id);
    delete sanitizedData.parents;

    setPending(true);

    saveCity({ variables: { city: sanitizedData } });
  };

  useSubscription(CITY_SAVED, {
    variables: { id: parseId(data.id) },
    onData: ({ data }) => {
      setPending(false);
      const city = data.data.citySaved.city;
      addToast(t('City.Saved'), 'success');
      shouldRefetchListVar(true);
      onCitySaved && onCitySaved(city);
    }
  });

  // #endregion

  const disabled = useMemo(() => !canWrite || pending, [canWrite, pending]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          required
          fullWidth
          label={t('City.Code')}
          value={lens.code |> get |> valueOrDefault('')}
          onChange={lens.code |> set}
          error={!isValid(validation.code)}
          helperText={getErrors(validation.code)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          required
          fullWidth
          label={t('City.Name')}
          value={lens.name |> get |> valueOrDefault('')}
          onChange={lens.name |> set}
          error={!isValid(validation.name)}
          helperText={getErrors(validation.name)}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={2}>
        <LabeledCheckbox label={t('City.Inactive')} checked={lens.inactive |> get} onChange={lens.inactive |> set} disabled={disabled} />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          required
          fullWidth
          label={t('City.NameInt')}
          value={lens.nameInt |> get |> valueOrDefault('')}
          onChange={lens.nameInt |> set}
          error={!isValid(validation.nameInt)}
          helperText={getErrors(validation.nameInt)}
          disabled={disabled}
        />
      </Grid>
      {data.type === CityType.Country && (
        <>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t('City.ISOAlpha2')}
              value={lens.isoAlpha2 |> get |> valueOrDefault('')}
              onChange={lens.isoAlpha2 |> set}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t('City.ISOAlpha3')}
              value={lens.isoAlpha3 |> get |> valueOrDefault('')}
              onChange={lens.isoAlpha3 |> set}
              disabled={disabled}
            />
          </Grid>
        </>
      )}
      {data.type !== CityType.Country && (
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            isNumeric
            thousandSeparator={false}
            decimalScale={0}
            label={t('City.Siruta')}
            value={lens.siruta |> get |> valueOrDefault('')}
            onChange={lens.siruta |> set}
            disabled={disabled}
          />
        </Grid>
      )}
      <Portal container={() => document.getElementById(actionsSlotId)}>
        <IconButton type='save' size={saveIconSize} onClick={handleSave} disabled={!isDirty || disabled} />
      </Portal>
    </Grid>
  );
};

CityEditComponent.propTypes = {
  data: PropTypes.object,
  actionsSlotId: PropTypes.string,
  saveIconSize: PropTypes.string,
  onCitySaved: PropTypes.func,
  onDirty: PropTypes.func
};

export default CityEditComponent;
